import { type Change, type KeyedNodeType, Schema, type ValidateNodeFn } from '@gitbook/slate';

import {
    annotation,
    buttons,
    code,
    columns,
    contentRef,
    divider,
    drawing,
    embed,
    emoji,
    expandable,
    expression,
    files,
    heading,
    hint,
    icon,
    ifBlock,
    images,
    integration,
    link,
    list,
    math,
    mention,
    openapi,
    openapiOperation,
    openapiSchemas,
    openapiWebhook,
    paragraph,
    prompt,
    quote,
    reusableContent,
    stepper,
    table,
    tabs,
    updates,
} from './node-apis';
import {
    BLOCK_CODE,
    BLOCK_CODE_LINE,
    BLOCK_DIVIDER,
    BLOCK_HEADING_1,
    BLOCK_HEADING_2,
    BLOCK_HEADING_3,
    BLOCK_LIST_ITEM,
    BLOCK_LIST_OL,
    BLOCK_LIST_UL,
    BLOCK_PARAGRAPH,
    BLOCK_QUOTE,
    BLOCK_REUSABLE_CONTENT,
    BLOCK_TABLE,
    BLOCKS,
    INLINE_EMOJI,
    INLINE_LINK,
    INLINE_MENTION,
    INLINES,
    MARK_BOLD,
    MARK_CODE,
    MARK_ITALIC,
    MARK_STRIKETHROUGH,
    MARK_SUBSCRIPT,
    MARK_SUPERSCRIPT,
    MARKS,
} from './types';
import { unwrapChildren, validateBlockCaption } from './utils';

/**
 * Version number for the document schema.
 * We started at 1. Document older than the beginning of the versioning are
 * considered 0.
 *
 * Whenever the document format changes in a breaking way, we should bump it.
 * We use this version number to know if we need to normalize a document before
 * rendering it.
 *
 * # Bump when:
 * - Adding a required data to an existing node type
 * - Removing existing node type (or renaming etc.)
 * - Adding new normalization rules affecting previously accessible and valid
 *   values.
 *
 * # Avoid bumping when:
 * - Adding optional data to an existing node type, that is default-handled in
 *   the code base.
 * - Adding new node types
 * - Adding new normalization rules affecting states that were not possible to
 *   obtain and save, without the app crashing. Said otherwise, no user should
 *   have been ever able to save such a value.
 */
export const SCHEMA_VERSION = 9;

/**
 * Schema to normalize a GitBook document.
 */
export const schema = Schema.create({
    validations: [
        validateSchemaVersion,
        validateStripUnknownNodes({ blocks: BLOCKS, inlines: INLINES, marks: MARKS }),
        validateBlockCaption,
    ],
}).combineWith([
    annotation.schema,
    code.schema,
    contentRef.schema,
    divider.schema,
    drawing.schema,
    embed.schema,
    emoji.schema,
    icon.schema,
    expression.schema,
    expandable.schema,
    files.schema,
    heading.schema,
    hint.schema,
    images.schema,
    integration.schema,
    buttons.schema,
    link.schema,
    list.schema,
    math.schema,
    mention.schema,
    paragraph.schema,
    prompt.schema,
    quote.schema,
    ifBlock.schema,
    reusableContent.schema,
    openapi.schema,
    openapiOperation.schema,
    openapiSchemas.schema,
    openapiWebhook.schema,
    table.schema,
    tabs.schema,
    updates.schema,
    stepper.schema,
    columns.schema,
]);

/**
 * Schema to normalize a GitBook document, with upcoming changes.
 */
export const schemaNext = schema;

/*
 * Minimalist editor schema for comments/prompts is a minimalist version of the document schema.
 * It aims at supporting basic markdown features.
 */
export const schemaBasic = Schema.create().combineWith([
    paragraph.schema,
    emoji.schema,
    mention.schemaReplaceLinks,
    mention.schema,
    list.schema,
    // Strip unknown nodes after validating the rest
    Schema.create({
        validations: [
            validateSchemaVersion,
            validateStripUnknownNodes({
                blocks: new Set([BLOCK_PARAGRAPH, BLOCK_LIST_UL, BLOCK_LIST_OL, BLOCK_LIST_ITEM]),
                inlines: new Set([INLINE_EMOJI, INLINE_MENTION]),
                marks: new Set([
                    MARK_BOLD,
                    MARK_CODE,
                    MARK_ITALIC,
                    MARK_STRIKETHROUGH,
                    MARK_SUPERSCRIPT,
                    MARK_SUBSCRIPT,
                ]),
            }),
        ],
    }),
]);

/*
 * Schema for a document displayed read-only outside the page editor: an agent's chat message, the
 * summary of a finding or of an insights topic. A superset of `schemaBasic` — everything a model
 * writes in Markdown, minus the blocks a narrow panel has no renderer for.
 *
 * Separate from `schemaBasic` because that one decides what is *stored*: comment bodies, change
 * request descriptions and agent instructions are normalized with it on their way into the
 * database. This one only decides what a reader is shown. Both strip whatever they do not list,
 * and a stripped block takes its text with it — which is why an agent's reply used to arrive with
 * its headings, code blocks and tables blank.
 */
export const schemaDisplay = Schema.create().combineWith([
    paragraph.schema,
    emoji.schema,
    mention.schema,
    link.schema,
    list.schema,
    heading.schema,
    code.schema,
    quote.schema,
    table.schema,
    divider.schema,
    // Strip unknown nodes after validating the rest
    Schema.create({
        validations: [
            validateSchemaVersion,
            validateStripUnknownNodes({
                blocks: new Set([
                    BLOCK_PARAGRAPH,
                    BLOCK_LIST_UL,
                    BLOCK_LIST_OL,
                    BLOCK_LIST_ITEM,
                    BLOCK_HEADING_1,
                    BLOCK_HEADING_2,
                    BLOCK_HEADING_3,
                    BLOCK_CODE,
                    BLOCK_CODE_LINE,
                    BLOCK_QUOTE,
                    BLOCK_TABLE,
                    BLOCK_DIVIDER,
                ]),
                inlines: new Set([INLINE_EMOJI, INLINE_MENTION, INLINE_LINK]),
                marks: new Set([
                    MARK_BOLD,
                    MARK_CODE,
                    MARK_ITALIC,
                    MARK_STRIKETHROUGH,
                    MARK_SUPERSCRIPT,
                    MARK_SUBSCRIPT,
                ]),
            }),
        ],
    }),
]);

/**
 * A set of blocks that are valid for reusable content.
 */
export const REUSABLE_CONTENT_BLOCKS = new Set(BLOCKS);
REUSABLE_CONTENT_BLOCKS.delete(BLOCK_REUSABLE_CONTENT);

/**
 * A set of blocks that can be turned into reusable content.
 */
export const TURNABLE_INTO_REUSABLE_CONTENT_BLOCKS = new Set(REUSABLE_CONTENT_BLOCKS);
// List items are allowed into a reusable content but we can't turn one into a reusable content.
TURNABLE_INTO_REUSABLE_CONTENT_BLOCKS.delete(BLOCK_LIST_ITEM);

/**
 * Schema for Reusable Content. It mirrors the document schema, but does not allow nested reusable content.
 */
export const schemaForReusableContent = Schema.create({
    validations: [
        validateSchemaVersion,
        validateStripUnknownNodes({
            blocks: REUSABLE_CONTENT_BLOCKS,
            inlines: INLINES,
            marks: MARKS,
        }),
        validateBlockCaption,
    ],
}).combineWith([
    annotation.schema,
    code.schema,
    contentRef.schema,
    divider.schema,
    drawing.schema,
    embed.schema,
    emoji.schema,
    icon.schema,
    expandable.schema,
    files.schema,
    heading.schema,
    hint.schema,
    images.schema,
    buttons.schema,
    link.schema,
    list.schema,
    math.schema,
    mention.schema,
    paragraph.schema,
    prompt.schema,
    quote.schema,
    ifBlock.schema,
    openapi.schema,
    updates.schema,
    stepper.schema,
    table.schema,
    tabs.schema,
    columns.schema,
    integration.schema,
]);

/**
 * Save the schema version along the document, to optimize deserialization from
 * the storage (no need to normalize again if up-to-date).
 */
function validateSchemaVersion(node: KeyedNodeType): ((change: Change) => void) | null {
    if (node.object !== 'document') {
        return null;
    }
    const document = node;

    const currentVersion = document.data.get('schemaVersion', 0);
    if (currentVersion === SCHEMA_VERSION) {
        return null;
    }

    return (change) => {
        change.setDataByKey(document.key, ['schemaVersion'], SCHEMA_VERSION);
    };
}

/**
 * Remove all unknown nodes.
 */
function validateStripUnknownNodes(accepted: {
    blocks: Set<string>;
    inlines: Set<string>;
    marks: Set<string>;
}): ValidateNodeFn {
    return (node) => {
        if (node.object === 'document' || node.object === 'fragment') {
            return null;
        }

        if (node.object === 'text') {
            const invalidMarks = node.getMarks().filter((mark) => !accepted.marks.has(mark.type));
            if (invalidMarks.size === 0) {
                return null;
            }

            return (change) => {
                invalidMarks.forEach((mark) => {
                    change.removeMarkByKey(node.key, 0, node.text.length, mark, {
                        normalize: false,
                    });
                });
            };
        }

        if (node.object === 'block' && accepted.blocks.has(node.type)) {
            return null;
        }

        if (node.object === 'inline' && accepted.inlines.has(node.type)) {
            return null;
        }

        return (change) => {
            change.withoutNormalization(() => {
                if (node.isVoid) {
                    change.removeNodeByKey(node.key, { normalize: false });
                } else {
                    unwrapChildren(change, node);
                }
            });
        };
    };
}
